import {
  CompletedProgrammeWorkout,
  HustlerProgramme,
  ProgrammeWorkout,
  ProgrammeWorkoutsAvailability,
  Workout,
} from '../dao/core-dao'
import { coreService } from '../service/core-service'
import { UseQueryOptions, UseQueryResult, useQuery } from '@tanstack/react-query'
import { queryKeys } from 'src/util/query-keys'

export const useHustlerProgramme = (
  programmeId: string,
  options?: Omit<UseQueryOptions<HustlerProgramme, Error>, 'queryKey' | 'queryFn'>,
): UseQueryResult<HustlerProgramme, Error> => {
  return useQuery<HustlerProgramme, Error>(
    queryKeys.hustlerProgramme(programmeId),
    () => coreService.getHustlerProgramme(programmeId as string),
    {
      enabled: !!programmeId,
      ...options,
    },
  )
}

export const useProgrammeWorkouts = (
  programmeId: string,
  options?: Omit<UseQueryOptions<ProgrammeWorkout[], Error>, 'queryKey' | 'queryFn'>,
): UseQueryResult<ProgrammeWorkout[], Error> => {
  return useQuery<ProgrammeWorkout[], Error>(
    queryKeys.programmeWorkouts(programmeId),
    () => coreService.getProgrammeWorkouts(programmeId),
    {
      enabled: !!programmeId,
      ...options,
    },
  )
}

export const useCompletedProgrammeWorkouts = (
  programmeId: string,
  options?: Omit<UseQueryOptions<CompletedProgrammeWorkout[], Error>, 'queryKey' | 'queryFn'>,
): UseQueryResult<CompletedProgrammeWorkout[], Error> => {
  return useQuery<CompletedProgrammeWorkout[], Error>(
    queryKeys.completedProgrammeWorkouts(programmeId),
    () => coreService.getMyCompletedProgrammeWorkoutsFromProgramme(programmeId),
    {
      enabled: !!programmeId,
      ...options,
    },
  )
}

export const useProgrammeWorkout = (
  programmeWorkout: ProgrammeWorkout,
  options?: Omit<UseQueryOptions<Workout, Error>, 'queryKey' | 'queryFn'>,
): UseQueryResult<ProgrammeWorkout & { workout: Workout }, Error> => {
  const workoutId = programmeWorkout?.workoutId

  const workoutQuery = useQuery<Workout, Error>(
    queryKeys.programmeWorkout(workoutId),
    () => coreService.getWorkoutById({ workoutId }),
    {
      enabled: !!workoutId,
      ...options,
    },
  )

  const data = workoutQuery.data && programmeWorkout ? { ...programmeWorkout, workout: workoutQuery.data } : undefined

  return {
    ...workoutQuery,
    data,
  } as UseQueryResult<ProgrammeWorkout & { workout: Workout }, Error>
}

export const useProgrammeWorkoutsAvailability = (
  programmeId: string,
  options?: Omit<UseQueryOptions<ProgrammeWorkoutsAvailability[], Error>, 'queryKey' | 'queryFn'>,
): UseQueryResult<ProgrammeWorkoutsAvailability[], Error> => {
  return useQuery<ProgrammeWorkoutsAvailability[], Error>(
    queryKeys.programmeWorkoutsAvailability(programmeId),
    () => coreService.getProgrammeWorkoutsAvailability(programmeId),
    {
      enabled: !!programmeId,
      ...options,
    },
  )
}
